import { AddOnType, FeatureType, GetTopicResponse, IntegrationException, MapSetUpOptions } from '../../types';
import { LitElement, css, html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators';
import { getSmallSocialImageForTopic, getSocialMediaImageForTopic, tinyLatitudeMove } from '../../helpers/files';
import { isDefined, undefinedIfEmpty } from '../../helpers/utils';

import { StateController } from '../../helpers/state/state-management/state-controller';
import { detectMobileViewport } from '../../helpers/device';
import featuresInfo from '../../helpers/state/features-info';
import { getEmbedCode } from '../../helpers/api/topics-api';
import { getMainPage } from '../../helpers/page-name';
import { getQRCode } from '../../helpers/urls';
import pointCache from '../../helpers/state/point-cache';
import topicData from '../../helpers/state/topic-data';

@customElement('map-sharing-component')
export class MapSharingComponent extends LitElement {


    @query('#modeOption')
    modeOption!: HTMLInputElement;

    @query('#viewOption')
    viewOption!: HTMLInputElement;

    @query('#modeOptionMobile')
    modeOptionMobile!: HTMLInputElement;

    @query('#viewOptionMobile')
    viewOptionMobile!: HTMLInputElement;

    @property({ type: String })
    crowdsourceLink: string = 'loading...';

    @property({ type: String })
    viewonlyLink: string = 'loading...';

    @property({ type: String })
    publishedCrowdsourceLink: string = 'loading...';

    @property({ type: String })
    publishedViewOnlyLink: string = 'loading...';

    @property({ type: Boolean })
    showEmbedOnly: boolean = false;

    @property({ type: String })
    socialMediaShareImage?: string;

    // for backpack share buttons
    @property({ type: Object })
    topic?: GetTopicResponse;

    @state()
    private showEmailInvite: boolean = false;

    @state()
    private showCopyLink: boolean = true;

    @state()
    private showEmbed: boolean = false;

    @state()
    private showQRCode: boolean = false;

    @state() qrCodeLink: string = '';

    @state()
    mode: string = 'view-only';

    @state()
    regeneratingPreview: boolean = false;

    @state()
    isPointLink: boolean = false;

    @state()
    hideCurrentView: boolean = false;

    @state()
    mapSetUpOptions? : MapSetUpOptions;

    @state()
    topicKey?: string;

    @state()
    selected_tab: string = 'Map';

    @state()
    options: string[] = ['Map', 'List / Itinerary', 'Crowdsourced Form'];

    @state()
    hideSummaryHeader: boolean = false;

    @state()
    clearEmailAfterEachSubmission: boolean = false;

    @state()
    hideMapLinkInHeader: boolean = false;

    /// Note this element is so messy and needs to be refactored at some point

    private readonly bindTopicState = new StateController(this, topicData);

    static amplitude_callsite: string = 'Map Sharing Component';

    amplitude_callsite: string = 'Share Map';

    get socialMediaImage() {
        return this.socialMediaShareImage;
    }

    protected async firstUpdated() {
        if (topicData.topic !== undefined) {
            this.publishedCrowdsourceLink = this.crowdsourceLink;
            this.publishedViewOnlyLink = this.viewonlyLink;
            this.socialMediaShareImage = getSmallSocialImageForTopic(topicData.topic, this.mapSetUpOptions);

            // if user is not an add pointer remove submission form tab
            if (!topicData.topic?.is_addpointer) {
                this.options = ['Map', 'List / Itinerary'];
                this.selected_tab = 'Map';
            }
        };
    }

    updated(changedProperties: Map<string | number | symbol, unknown>) {
        if (changedProperties.has('crowdsourceLink') && this.crowdsourceLink !== 'loading...' && isDefined(undefinedIfEmpty(this.crowdsourceLink)) ) {
            this.publishedCrowdsourceLink = this.crowdsourceLink;
        }

        if (changedProperties.has('viewonlyLink') && this.viewonlyLink !== 'loading...' && isDefined(undefinedIfEmpty(this.viewonlyLink)) ) {
            this.publishedViewOnlyLink = this.viewonlyLink;
        }

        if (changedProperties.has('mode') && this.mode === 'collaborative') {
            this.showEmbedOnly = false;
            this.modeOption.checked = true;
        }
    }

    private replaceHost(url: string): string {
        if (isDefined(topicData.verifiedAlternateHost) && url !== 'loading...' && isDefined(undefinedIfEmpty(url)) ) {
            const urlObject = new URL(url);
            urlObject.host = topicData.verifiedAlternateHost;
            return urlObject.toString();
        }
        return url;
    }

    private async toggleHideListHeader(e: any) {
        this.hideSummaryHeader = e.target.checked;
        this.requestUpdate();
    }

    private async toggleMode(e: any) {
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        if (detectMobileViewport()) {
            this.mode = this.modeOptionMobile.checked ? 'collaborative' : 'view-only';
            this.qrCodeLink = await getQRCode(
                MapSharingComponent.amplitude_callsite,
                this.mode === 'view-only' ? this.publishedViewOnlyLink : this.publishedCrowdsourceLink
            );
        } else {
            this.mode = this.modeOption.checked ? 'collaborative' : 'view-only';
            this.qrCodeLink = await getQRCode(
                MapSharingComponent.amplitude_callsite,
                this.mode === 'view-only' ? this.publishedViewOnlyLink : this.publishedCrowdsourceLink
            );
        }
        this.requestUpdate();
    }

    private async openUpgradeDialog() {
        this.dispatchEvent(
            new CustomEvent('open-upgrade-dialog', {
                detail: {
                    featureType: FeatureType.ShareMap,
                },
            })
        );
    }

    private embedClick() {
        this.showEmbed = true;
        this.showCopyLink = false;
        this.showQRCode = false;
        this.showEmailInvite = false;
    }

    private emailClick() {
        this.showEmailInvite = true;
        this.showCopyLink = false;
        this.showEmbed = false;
        this.showQRCode = false;
    }

    private linkClick() {
        this.showCopyLink = true;
        this.showEmbed = false;
        this.showEmailInvite = false;
        this.showQRCode = false;
    }

    private async qrCodeClick(link:string) {
        this.qrCodeLink = await getQRCode(
            MapSharingComponent.amplitude_callsite,
            link
        );
        this.showQRCode = true;
        this.showCopyLink = false;
        this.showEmbed = false;
        this.showEmailInvite = false;
        this.requestUpdate();
    }

    private async regeneratePreview() {
        if (topicData.topic !== undefined) {
            const updated_topic = await tinyLatitudeMove(topicData.topic);
            const image = getSmallSocialImageForTopic(updated_topic, this.mapSetUpOptions);
            // also recreate full sized in the background so it is updated in thumio
            getSocialMediaImageForTopic(updated_topic, this.mapSetUpOptions);
            this.socialMediaShareImage = image;

            // turn on regenerating preview for 3 seconds
            this.regeneratingPreview = true;
            setTimeout(() => {
                this.regeneratingPreview = false;
            }, 20000);
        }
    }

    private async toggleView() {

        if (this.viewOption.checked || this.viewOptionMobile.checked) {
            // get map lat, long, zoom

            const map = pointCache.map;
            if (map === undefined) {
                return;
            }
            const center = map.getCenter();
            const zoom = map.getZoom();
            const lat = center?.lat();
            const lng = center?.lng();

            // get filter list for tags and categories
            const tags = pointCache.tagFilterList.join(',');
            const categories = pointCache.categoryFilterList.join(',');

            // construct params
            const params = new URLSearchParams();
            // get existing params
            const url = new URL(window.location.href);
            const existingParams = url.searchParams;
            existingParams.forEach((value, key) => {
                params.append(key, value);
            });
            if (lat !== undefined) {
                params.append('lat', lat.toString());
            }
            if (lng !== undefined) {
                params.append('lng', lng.toString());
            }
            if (zoom !== undefined) {
                params.append('zoom', zoom.toString());
            }
            if (tags !== undefined && tags !== '') {
                params.append('tags', tags);
            }
            if (categories !== undefined && categories !== '') {
                params.append('categories', categories);
            }


            // get url
            this.publishedViewOnlyLink = this.publishedViewOnlyLink.split('?')[0] + `?${params.toString()}`;
            this.publishedCrowdsourceLink = this.publishedCrowdsourceLink.split('?')[0] + `?${params.toString()}`;

            this.mapSetUpOptions = {
                center_lat: lat,
                center_lng: lng,
                zoom,
                selectedTags: pointCache.tagFilterList,
                focusPointId: undefined,
                selectedCategories: pointCache.categoryFilterList,
                focusChallengeLocationId: undefined,
            };

            this.socialMediaShareImage = `${this.socialMediaShareImage?.split('?')[0] ?? ''}?${params.toString()}`;

            this.requestUpdate();
        } else {
            this.publishedViewOnlyLink = this.publishedViewOnlyLink.split('?')[0];
            this.publishedCrowdsourceLink = this.publishedCrowdsourceLink.split('?')[0];

            this.mapSetUpOptions = undefined;

            this.socialMediaShareImage = this.socialMediaShareImage?.split('?')[0];
            this.requestUpdate();
        }
    }

    handleChangeTab(e: CustomEvent) {
        this.tabIndex = e.detail.tabIndex;
        this.selected_tab = this.options[this.tabIndex];
        this.requestUpdate();
    }

    async onUpgradeClick() {
        const addon = this.getCustomFormAddOn();
        if (addon === undefined || addon === null) {
            getMainPage().errorToastElement.showToast('Could not find the Crowdsourcing & Field Customization Add On. Please try again later.');
            return;
        }
        await topicData.installAddon(this.amplitude_callsite, addon);
    }

    getCustomFormAddOn() {
        return topicData.allAddons.find((a) => a.component === AddOnType.CustomForm);
    }

    hasCustomFormAddon() {
        const addon = this.getCustomFormAddOn();
        return addon !== undefined && topicData.activatedAddons.includes(addon);
    }

    // TODO: this is awful and needs to be refactored
    protected render() {
        const topic = this.topic ?? topicData.topic;
        const topic_key = this.topicKey && this.topicKey !== '' ? this.topicKey : topicData.topicKey;
        const hasEntitlement = topic?.is_writer === true && featuresInfo.shareMapEnabled || topic?.is_writer === false || topicData.hasExceptions === IntegrationException.WelcomeHomeRelocations;
        const title = this.mode === 'view-only' ? this.isPointLink ? `Share ${topic?.point_name ?? 'Point'}` :
            `Share ${this.selected_tab === 'Map' ? 'Interactive Map' : this.selected_tab === 'List / Itinerary' ? 'List / Itinerary' : 'Point Submission Form'}` :
            `Share  ${this.selected_tab === 'Map' ? 'Collaborative Interactive Map' : this.selected_tab === 'List / Itinerary' ? 'List / Itinerary' : 'Point Submission Form'}`;
        let link = this.mode === 'view-only' ? this.publishedViewOnlyLink : this.publishedCrowdsourceLink;
        if (this.selected_tab === 'List / Itinerary') {
            // add param of view=summary to link
            const url = new URL(link);
            url.searchParams.append('view', 'summary');
            url.searchParams.append('hide_summary_header', this.hideSummaryHeader ? 'true' : 'false');
            link = url.toString();
        }
        if (this.selected_tab === 'Crowdsourced Form' && topic?.is_addpointer === true) {
            // use crowdsource link for submission form
            link = this.publishedCrowdsourceLink;
            // add param of view=form to link
            const url = new URL(link);
            url.searchParams.append('view', 'form');
            if (this.hideMapLinkInHeader) {
                url.searchParams.append('hide_map_link', 'true');
            }
            link = url.toString();
        }

        if (this.clearEmailAfterEachSubmission) {
            // add param of view=point to link
            const url = new URL(link);
            url.searchParams.append('clear_emails', this.clearEmailAfterEachSubmission ? 'true' : 'false');
            link = url.toString();
        }
        link = isDefined(topicData.verifiedAlternateHost) ? this.replaceHost(link) : link;
        const embedLink = getEmbedCode(MapSharingComponent.amplitude_callsite, link, topicData.topic?.disable_powered_by_proxi ?? false);
        const printableLink = topicData.topicKey === undefined ? `/#print?topic_id=${topicData.topicId}` : `/#print?topic_id=${topicData.topicId}&topic_key=${topicData.topicKey}`;
        const embedSuccessMessage =
            this.mode === 'view-only'
                ? 'View-only embed code copied successfully.'
                : 'Crowdsource embed code copied successfully.';
        const embedPanelTitle =
            this.mode === 'view-only'
                ? 'Embed View-Only Map in Website or App'
                : 'Embed Collaborative Map in Website or App';

        return html`
            <div class="modalContent desktopOnly">
                <div class="modalHeader flexContainer alignCenter">
                    <div class="leftSection">
                        <h2 class="modalTitle">${title}</h2>
                        ${topic?.is_writer === true ? html`
                            <tabs-component
                                .options=${this.options}
                                .tabIndex=${this.selected_tab === 'Map' ? 0 : this.selected_tab === 'List / Itinerary' ? 1 : 2}
                                @on-select-tab=${this.handleChangeTab}
                            ></tabs-component>
                        ` : ''}
                        <div class="toggleSection flexContainer ${topicData.hasExceptions === IntegrationException.WelcomeHomeRelocations ? 'hidden' :''}">
                            ${topic?.is_writer === true && this.selected_tab === 'Map' ? html`
                                <div class="checkbox-section">
                                    <mwc-checkbox
                                        id="modeOption"
                                        name="modeOption"
                                        @change="${this.toggleMode}">
                                    </mwc-checkbox>
                                    <form-tooltip
                                        tipwidth="180px"
                                        leftPadding="30%"
                                        message="Viewers can interact with the map and suggest points to add to the map. You can update collaboration settings, like point approval, in 'share settings'">
                                        <div class="optionItem flexContainer alignCenter">
                                            <span class="optionLabel">Let Viewers ${topic?.requires_approval ? 'Suggest' : 'Add'} ${topic?.point_name ?? 'Point'}s</span>
                                        </div>
                                    </form-tooltip>
                                </div>
                            ` : ''}
                            ${this.mode === 'collaborative' || this.selected_tab === 'Crowdsourced Form' ? html`
                                <div class="checkbox-section">
                                    <mwc-checkbox
                                        id="clearEmailOption"
                                        name="clearEmailOption"
                                        @change="${(e: any) => {
                                            this.clearEmailAfterEachSubmission = e.target.checked;
                                            this.requestUpdate();
                                        }}">
                                    </mwc-checkbox>
                                    <form-tooltip
                                        tipwidth="180px"
                                        leftPadding="30%"
                                        message="Clear email after each submission. This is primarily used for settings where many different user are submitting points from the same device.">
                                        <div class="optionItem flexContainer alignCenter">
                                            <span class="optionLabel">Clear Email After Each Submission</span>
                                        </div>
                                    </form-tooltip>
                                </div>
                                ${this.hasCustomFormAddon() && topicData.isOwner && this.selected_tab !== 'List / Itinerary' ? html`
                                    <span class="optionLabel" style="line-height: normal; white-space: wrap">
                                    <b>
                                    Want to customize the form your users use to add points? <a @click="${this.onUpgradeClick}" style="color: #007bff; text-decoration: underline; cursor: pointer;">Try The Crowdsourcing & Field Customization Add On</a>
                                    </b>
                                    </span>
                                ` : ''}
                            ` : ''}
                            ${this.selected_tab === 'Crowdsourced Form' ? html`
                                <div class="checkbox-section">
                                    <mwc-checkbox
                                        id="showMapLinkOption"
                                        name="showMapLinkOption"
                                        @change="${(e: any) => {
                                            this.hideMapLinkInHeader = e.target.checked;
                                            this.requestUpdate();
                                        }}">
                                    </mwc-checkbox>
                                    <form-tooltip
                                        tipwidth="180px"
                                        leftPadding="30%"
                                        message="Show the map link in the header of the submission form. Hide this to remove the map link.">
                                        <div class="optionItem flexContainer alignCenter">
                                            <span class="optionLabel">Hide Map Link in Header</span>
                                        </div>
                                    </form-tooltip>
                                </div>
                            ` : ''}
                            ${topic?.is_writer === true && this.selected_tab === 'List / Itinerary' ? html`
                                <div class="checkbox-section">
                                    <mwc-checkbox
                                        id="hideHeaderOption"
                                        name="hideHeaderOption"
                                        @change="${this.toggleHideListHeader}">
                                    </mwc-checkbox>
                                    <form-tooltip
                                        tipwidth="180px"
                                        leftPadding="30%"
                                        message="Hide the header and show only the list of points.">
                                        <div class="optionItem flexContainer alignCenter">
                                            <span class="optionLabel">Hide Header on Page</span>
                                        </div>
                                    </form-tooltip>
                                </div>
                            ` : ''}

                            ${this.isPointLink || this.hideCurrentView || this.selected_tab !== 'Map' ? '' : html`
                                <div class="checkbox-section">
                                    <mwc-checkbox
                                        id="viewOption"
                                        name="viewOption"
                                        @change="${this.toggleView}">
                                    </mwc-checkbox>
                                    <form-tooltip
                                        tipwidth="180px"
                                        leftPadding="30%"
                                        message="Share the map with the same area and filters you are currently on.">
                                        <div class="optionItem flexContainer alignCenter">
                                            <span class="optionLabel">Share with Current View</span>
                                        </div>
                                    </form-tooltip>
                                </div>
                            `}
                        </div>
                    </div>
                    ${this.selected_tab === 'Map' ? html`
                        <div class="rightSection">
                            ${this.regeneratingPreview ?
                            html`
                                <loading-element
                                    loadingString="Regenerating Image">
                                </loading-element>
                            `: html`
                                <div class="imageContainer">
                                    <img class="previewImg" src="${this.socialMediaImage ?? ''}" alt="Share Map" />
                                </div>
                                ${topicData.isWriter ? html`
                                    <span class="refreshPreviewLink" @click="${this.regeneratePreview}">
                                        Regenerate Preview Image
                                    </span>
                                    <span class="helpertext"> (this can take a few seconds)</span>
                                ` : ''}
                            `}
                        </div>
                    ` : ''}
                </div>
                ${hasEntitlement ? '' : html`
                    <span class="upgrade_button" @click="${async () => await this.openUpgradeDialog()}"><img class="leftPng" src="/images/tiers/starter_small.png" style="height: 1rem; width:1rem;"  />
                    Start a free trial to share your map</span>
                `}
                <div class="modalBody ${hasEntitlement ? '' : 'gated'}">
                    ${this.showEmbedOnly
                        ? html`
                            <div class="modalSubTitle">${embedPanelTitle}</div>
                            <copy-link-component
                                text="${embedLink}"
                                successMessage=${embedSuccessMessage}
                                textType="Embed"
                                pageName="Admin Portal">
                                Copy Code
                            </copy-link-component>
                        `
                        : html`
                            <social-share-buttons
                                shareLink="${link}"
                                printableLink="${printableLink}"
                                .hideLinkButton="${this.showCopyLink}"
                                @open-upgrade-dialog=${this.openUpgradeDialog}
                                ?hideQrCodeButton="${this.isPointLink}"
                                .hidePrintButton="${!topicData.isWriter}"
                                @embed-click=${this.embedClick}
                                @email-click=${this.emailClick}
                                @link-click=${this.linkClick}
                                @qr-code-click=${async () => await this.qrCodeClick(link)}
                                size="sm"
                                scrollable>
                            </social-share-buttons>
                            ${this.showEmailInvite ? html`
                                <emails-invite-component
                                    mode="${this.mode}"
                                    .topic_id="${topic?._id.$oid}"
                                    .topic_key="${topic_key}">
                                    Send
                                </emails-invite-component>
                            ` : ''}
                            ${this.showCopyLink ? html`
                                <copy-link-component
                                    text="${link}"
                                    successMessage="Link copied successfully."
                                    textType="Share"
                                    pageName="Admin Portal">
                                    Copy Link
                                </copy-link-component>
                            ` : ''}
                            ${this.showEmbed ? html`
                                <copy-link-component
                                    text="${embedLink}"
                                    successMessage=${embedSuccessMessage}
                                    textType="Embed"
                                    pageName="Admin Portal">
                                    Copy Code
                                </copy-link-component>
                            ` : ''}
                            ${this.showQRCode ? html`
                                <div class="qrCodeSection">
                                    <img src="${this.qrCodeLink}" alt="QR Code" />
                                    <a href="${this.qrCodeLink}" download="qr-code.png">
                                        <mwc-icon>download</mwc-icon>
                                        Download QR Code
                                    </a>
                                </div>
                            ` : ''}

                        `
                    }
                </div>
            </div>
            <div class="modalContent mobileOnly">
                <div class="modalHeader flexContainer alignCenter">
                    <h2 class="modalTitle"> Share ${topicData.name}</h2>
                    <div class="toggleSection flexContainer alignCenter ${topicData.hasExceptions === IntegrationException.WelcomeHomeRelocations ? 'hidden' :''}">
                        <div class="checkbox-section">
                            ${topicData.isWriter ? html`
                                <mwc-checkbox
                                    id="modeOptionMobile"
                                    reducedTouchTarget
                                    name="modeOption"
                                    @change="${this.toggleMode}">
                                </mwc-checkbox>
                                <form-tooltip
                                    tipwidth="180px"
                                    leftPadding="30%"
                                    message="Viewers can interact with the map and suggest points to add to the map. You can update collaboration settings, like point approval, in “share settings”.">
                                    <div class="optionItem flexContainer alignCenter">
                                        <span class="optionLabel">Allow Viewers to ${topic?.requires_approval === true ? 'Suggest' : 'Add'} Points</span>
                                    </div>
                                </form-tooltip>
                            ` : ''}
                        </div>
                        ${this.isPointLink || this.hideCurrentView ? '' : html`
                            <div class="checkbox-section">
                                <mwc-checkbox
                                    id="viewOptionMobile"
                                    reducedTouchTarget
                                    name="viewOption"
                                    @change="${this.toggleView}">
                                </mwc-checkbox>
                                <form-tooltip
                                    tipwidth="180px"
                                    leftPadding="30%"
                                    message="Share the map with the same area and filters you are currently on.">
                                    <div class="optionItem flexContainer alignCenter">
                                        <span class="optionLabel">Share with Current View</span>
                                    </div>
                                </form-tooltip>
                            </div>
                        `}
                    </div>
                </div>
                <div class="modalBody">
                    <social-share-buttons shareLink="${link}" size="sm"
                        printableLink="${printableLink}"
                        ?hideqrcodebutton="${this.isPointLink}"
                        .hideLinkButton="${this.showCopyLink}"
                        @open-upgrade-dialog=${this.openUpgradeDialog}
                        @embed-click=${this.embedClick}
                        @email-click=${this.emailClick}
                        @link-click=${this.linkClick}
                        @qr-code-click=${async () => await this.qrCodeClick(link)}
                    ></social-share-buttons>
                    ${this.showEmailInvite ? html`
                                <emails-invite-component
                                    mode="${this.mode}"
                                    .topic_id="${topic?._id.$oid}"
                                    .topic_key="${this.topicKey}">
                                    Send
                                </emails-invite-component>
                            ` :
                            this.showCopyLink ? html`
                                <copy-link-component
                                    text="${link}"
                                    successMessage="Link copied successfully."
                                    textType="Share"
                                    pageName="Admin Portal">
                                    Copy Link
                                </copy-link-component>
                            ` :
                            this.showEmbed ? html`
                                <copy-link-component
                                    text="${embedLink}"
                                    successMessage=${embedSuccessMessage}
                                    textType="Embed"
                                    pageName="Admin Portal">
                                    Copy Code
                                </copy-link-component>
                            ` :
                            this.showQRCode ? html`
                                <div class="qrCodeSection">
                                    <img src="${this.qrCodeLink}" alt="QR Code" />
                                    <a href="${this.qrCodeLink}" download="qr-code.png">
                                        <mwc-icon>download</mwc-icon>
                                        Download QR Code
                                    </a>
                                </div>
                            ` : ''}
                </div>
            </div>
            <error-toast id="errorToast"></error-toast>
        `;
    }

    static get styles() {
        return css`
            :host {
                --mdc-checkbox-state-layer-size: 24px;
                --mdc-checkbox-size: 24px;
            }

            .modalContent {
                padding: 1rem;
            }

            .modalBody {
                display: flex;
                flex-direction: column;
            }

            .modalHeader {
                justify-content: space-between;
                margin-bottom: 1.5rem;
                display: flex;
                flex-direction: row;
                gap: 10px;
            }

            .modalTitle {
                color: var(--main-primary-color);
                font-size: 1.5rem;
                margin-bottom: 0.5rem;
            }

            .modalSubTitle {
                font-family: var(--lumo-font-family);
                font-size: 1.25rem;
                font-weight: 500;
                line-height: 1.5rem;
                color: var(--theme-input-color);
            }

            .topicName {
                margin-bottom: 1rem;
            }

            .leftSection {
                display: flex;
                flex-direction: column;
                align-items: flex-start;
            }

            .imageContainer {
                display: flex;
                justify-content: center;
                align-items: center;
                width: 100%;
                max-height: 6rem;
                overflow: hidden;
                border-radius: 4px;
            }

            .previewImg {
                max-width: 100%;
                max-height: 100%;
            }

            .checkbox-section {
                display: flex;
                flex-direction: row;
                align-items: center;
            }

            .qrCodeSection {
                display: flex;
                flex-direction: row;
                align-items: center;
                margin-top: 1rem;
                color: var(--theme-input-color);
                justify-content: center;
            }

            .qrCodeSection a {
                margin-left: 1rem;
                color: var(--theme-input-color);
                text-decoration: none;
                display: flex;
                align-items: center;
            }

            .refreshPreviewLink {
                color: var(--theme-input-color);
                font-size: 0.75rem;
                cursor: pointer;
                text-decoration: underline;
                text-align: center;
            }

            .helpertext {
                font-size: 0.6rem;
                text-decoration: none;
            }

            .qrCodeSection img {
                width: 100px;
                height: 100px;
            }

            .toggleSection {
                padding-top: 0.25rem;
                justify-content: center;
                display: flex;
                flex-direction: column;
            }

            .flexContainer {
                display: flex;
            }

            .upgrade_button {
                cursor: pointer;
                text-decoration: underline;
            }

            .alignCenter {
                align-items: center;
            }

            .optionLabel {
                color: var(--theme-input-color);
                font-size: 0.75rem;
                white-space: nowrap;
                margin-right: 0.5rem;
            }

            .rightSection {
                display: flex;
                flex-direction: column;
                align-content: center;
                align-items: center;
                justify-content: flex-start;
                max-width:200px;
                line-height: 1.2rem;
            }

            .embedPanelSection {
                --lumo-secondary-text-color: var(--theme-input-color);
                --lumo-font-size-m: 1.25rem;
                border: 1px solid var(--theme-border-color);
                box-shadow: 0px 2px 2px -2px rgba(31, 27, 45, 0.08),
                    0px 4px 12px rgba(31, 27, 45, 0.08);
                padding: 0.75rem 1rem;
                border-radius: 0.75rem;
                margin-top: 2rem;
            }

            .embedDescription {
                margin: 1rem 0;
                max-width: 580px;
                color: var(--theme-input-color);
                font-size: 1rem;
            }

            copy-link-component {
                margin-bottom: 0.5rem;
            }

            .cardImg {
                margin-right: 1rem;
                height: 2.5rem;
            }

            .hidden {
                display: none;
            }

            .gated {
                pointer-events: none;
                opacity: 0.5;
                border: 1px solid var(--theme-border-color);
            }

            @media (max-width: 960px) {
                .flexContainer {
                    flex-direction: column;
                }

                .modalHeader {
                    justify-content: center;
                    gap: 0;
                }
                .modalTitle {
                    text-align: center;
                    font-size: 1.2rem;
                    margin-top: 0;
                    margin-bottom: 1rem;
                }

                .toggleSection {
                    display: flex;
                    flex-direction: column;
                    height: 2rem;

                }
            }

            @media (min-width: 551px) {
                .desktopOnly {
                    display: block;
                }

                .mobileOnly {
                    display: none;
                }

                social-share-buttons {
                    margin-bottom: 1.5rem;
                }
            }

            @media (max-width: 550px) {
                .desktopOnly {
                    display: none;
                }

                .mobileOnly {
                    display: block;
                }

                social-share-buttons {
                    max-width: 100%;
                    overflow-x: auto;
                }

                .modalContent {
                    padding: 0;
                    padding-bottom: 20px;
                    overflow-y: auto;
                    max-width: 90vw;
                }
            }
        `;
    }
}
